import React, { useState, useEffect, useCallback } from 'react';
import '../../../App.css';
import edit from './../../../images/edit.svg';
import dropdown from './../../../images/dropdown.svg';
import Select, { components } from 'react-select';
import { talentService, locationService, companyGroupService, companyService } from '../../../api/apiService';

// Format timestamp helper
const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Define the TruncatedContent component with company rating support
const TruncatedContent = ({ content, type, isGoodPerformer = false, isBadPerformer = false, isExpanded = false, onToggle }) => {
  if (!content) return null;

  let displayContent;
  if (type === "notes") {
    displayContent = content;
  } else if (type === "companies") {
    // Handle company data with timestamps and ratings
    if (isExpanded) {
      // When expanded, show each company on a separate line with full details
      if (Array.isArray(content)) {
        return (
          <div onClick={onToggle} style={{
            color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
            cursor: 'pointer',
            display: 'block',
            width: '100%',
            fontSize: '0.85rem' // Slightly bigger font for companies
          }}>
            {content.map((c, index) => {
              const companyName = c.label || c.name || c;
              const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
              const rating = c.rating ? ` [Rating ${c.rating}]` : '';
              return (
                <div key={index} style={{ 
                  marginBottom: '2px',
                  padding: '1px 2px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '0.85rem', // Same font size for individual companies
                  fontWeight: '300' // Remove bold styling
                }}>
                  {companyName}{rating}{timestamp}
                </div>
              );
            })}
          </div>
        );
      } else {
        displayContent = content;
      }
    } else {
      // When collapsed, show truncated list
      displayContent = Array.isArray(content)
        ? content.map(c => {
            const companyName = c.label || c.name || c;
            const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
            const rating = c.rating ? ` [Rating ${c.rating}]` : '';
            return `${companyName}${rating}${timestamp}`;
          }).join(", ")
        : content;
    }
  } else if (type === "locations") {
    // Handle location data
    displayContent = Array.isArray(content)
      ? content.map(loc => loc.label || loc.name || loc.short || loc).join(", ")
      : content;
  } else {
    displayContent = Array.isArray(content)
      ? content.map(c => c.label || c.name || c).join(", ")
      : content;
  }

  const style = {
    color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
    cursor: 'pointer',
    display: 'inline-block',
    width: '100%',
    fontSize: '0.85rem' // Same font size for all content
  };

  return (
    <div onClick={onToggle} style={style}>
      {isExpanded ? displayContent : (displayContent.length > 40 ? `${displayContent.substring(0, 40)}...` : displayContent)}
    </div>
  );
};

// Add DropdownIndicator component
const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <div style={{ width: "16px", margin: "0" }}>
          <svg
            version="1.1"
            id="Isolationsmodus"
            x="0px"
            y="0px"
            viewBox="0 0 322.9 257.78"
            shapeRendering="geometricPrecision"
          >
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z"
            />
          </svg>
        </div>
      </components.DropdownIndicator>
    )
  );
};

// Add positions options
const positions = [
  { value: "Hair & Make-Up", label: "Hair & Make-Up" },
  { value: "Art Direktion", label: "Art Direktion" },
  { value: "Foto", label: "Foto" },
  { value: "Foto Assist", label: "Foto Assist" },
  { value: "Video", label: "Video" },
  { value: "Digi-OP", label: "Digi-OP" },
  { value: "Styling", label: "Styling" },
  { value: "Styling Assist", label: "Styling Assist" },
  { value: "LOGISTIK / WARENAUFBEREITUNG", label: "LOGISTIK / WARENAUFBEREITUNG" },
  { value: "PRODUCER", label: "PRODUCER" },
  { value: "Art Buying", label: "Art Buying" }, 
  { value: "Account Management", label: "Account Management" },
  { value: "AI Content Production", label: "AI Content Production" }
];

// Add notes options
const notesOption = [
  {value: 'LEGER', label:'LEGER'},
  {value: 'HÄNGER', label:'HÄNGER'},
  {value: 'BÜSTE', label:'BÜSTE'},
  {value: 'ACC', label:'ACC'},
  {value: 'On Model', label:'On Model'},
  {value: 'Free text', label:'Free text'}
];

// Add rating options
const ratingOptions = [
  { value: 1, label: 'Rating 1' },
  { value: 2, label: 'Rating 2' },
  { value: 3, label: 'Rating 3' }
];

function TalentListPlaniing() {
  const [search, setSearch] = useState("");
  const [talents, setTalents] = useState([]);
  const [allTalents, setAllTalents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTalent, setCurrentTalent] = useState(null);
  const [currentName, setCurrentName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentFunction, setCurrentFunction] = useState("");
  const [currentSurname, setCurrentSurname] = useState("");
  const [currentPhone, setCurrentPhone] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [selectedGoodGroups, setSelectedGoodGroups] = useState([]);
  const [selectedBadGroups, setSelectedBadGroups] = useState([]);
  const [selectedtryoutGroups, setSelectedtryoutGroups] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [notesProductLeads, setNotesProductLeads] = useState('');
  const [notesProductLeadsdd, setNotesProductLeadsdd] = useState(null);
  
  // Individual company management for editing
  const [selectedGoodCompanies, setSelectedGoodCompanies] = useState([]);
  const [selectedBadCompanies, setSelectedBadCompanies] = useState([]);
  const [selectedtryoutCompanies, setSelectedtryoutCompanies] = useState([]);

  // Filters
  const [selectedType, setSelectedType] = useState("");
  const [selectedFunction, setSelectedFunction] = useState("");
  const [filterGoodGroups, setfilterGoodGroups] = useState([]);
  const [filterBadGroups, setfilterBadGroups] = useState([]);
  const [filtertryoutGroups, setfilterTyoutGroups] = useState([]);
  const [filterLocation, setfilterloation] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  // Add state for options
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [individualCompanies, setIndividualCompanies] = useState([]); // For main companies list

  // Styles for talent input titles
const titleStyles = {
  base: {
    marginLeft: '0'
  },
  good: {
    marginLeft: '0',
    color: '#37b24d',
    fontWeight: 'bold'
  },
  bad: {
    marginLeft: '0',
    color: '#ff6b6b',
    fontWeight: 'bold'
  }
};

// Custom style for selects
  const customStyles = {
    control: (base) => ({
      ...base,
      minWidth: "150px",
      borderColor: "black",
      borderWidth: "1px",
      boxShadow: "none",
      margin: "5px",
      borderRadius: "0px",
      color: "black",
      fontWeight: "700",
      textTransform: "uppercase",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "black" : "inherit",
      "&:hover": { backgroundColor: state.isSelected ? "black" : "#f7f7f7" },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      padding: '2px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#495057',
      fontSize: '0.85em'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#dc3545',
      ':hover': {
        backgroundColor: 'transparent',
        color: '#dc3545'
      }
    }),
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
  };

  // Update fetchTalents to fetch all data at once
  const fetchTalents = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch all talents without filters
      const response = await talentService.getAllTalents(1, 1000);
      
      // Handle response format
      const talentsData = response?.data?.talents || response?.data || [];
      
      if (Array.isArray(talentsData)) {
        // For each talent, fetch company relationships from the new endpoint
        const enhancedTalentsData = await Promise.all(talentsData.map(async (talent) => {
          try {
            // Try to get company data from the new endpoint
            const companyResponse = await talentService.getTalentCompanies(talent.id);
            
            // Check for nested data structure
            if (companyResponse && companyResponse.data) {
              const companyData = companyResponse.data;
              
              // Format locations to ensure they are in the correct format (short codes)
              const formattedLocations = (talent.locations || []).map(loc => {
                if (typeof loc === 'string') {
                  return loc;
                } else if (typeof loc === 'object' && loc !== null) {
                  // Try to extract a string value from the object
                  return loc.short || loc.name || loc.label || loc.city || JSON.stringify(loc);
                }
                return String(loc || ''); // Convert any other type to string
              }).filter(Boolean);
              
              // Merge the company data with the talent data
              const enhancedTalent = {
                ...talent,
                locations: formattedLocations,
                goodPerformanceCompany: companyData.goodPerformanceCompany || [],
                badPerformanceCompany: companyData.badPerformanceCompany || [],
                tryOutCompany: companyData.tryOutCompany || []
              };
              return enhancedTalent;
            }
            
            // If endpoint fails, return original talent data with formatted locations
            const formattedLocations = (talent.locations || []).map(loc => {
              if (typeof loc === 'string') {
                return loc;
              } else if (typeof loc === 'object' && loc !== null) {
                // Try to extract a string value from the object
                return loc.short || loc.name || loc.label || loc.city || JSON.stringify(loc);
              }
              return String(loc || ''); // Convert any other type to string
            }).filter(Boolean);
            
            return {
              ...talent,
              locations: formattedLocations
            };
          } catch (error) {
            // Just return the original talent data if there's an error
            // console.warn(`Could not fetch company data for talent ${talent.id}:`, error);
            
            // Format locations even in error case
            const formattedLocations = (talent.locations || []).map(loc => {
              if (typeof loc === 'string') {
                return loc;
              } else if (typeof loc === 'object' && loc !== null) {
                // Try to extract a string value from the object
                return loc.short || loc.name || loc.label || loc.city || JSON.stringify(loc);
              }
              return String(loc || ''); // Convert any other type to string
            }).filter(Boolean);
            
            return {
              ...talent,
              locations: formattedLocations
            };
          }
        }));
        
        setAllTalents(enhancedTalentsData);
      } else {
        console.error('Unexpected response format:', response);
        setAllTalents([]);
      }
    } catch (error) {
      console.error('Error fetching talents:', error);
      setAllTalents([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      const response = await companyGroupService.getAllGroups(1, 1000);
      const groupsData = response?.data?.data?.groups || response?.data || [];
      
      const formattedGroups = groupsData
        .map(group => ({
          value: group.id,
          label: group.name,
          companies: group.companies // Store the companies for later use
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setCompanyOptions(formattedGroups);
    } catch (error) {
      console.error('Error fetching company groups:', error);
    }
  };

  // Fetch individual companies from main companies list
  const fetchIndividualCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies(1, 1000);
      const companiesData = response?.data?.companies || response?.data || [];
      
      const formattedCompanies = companiesData
        .map(company => ({
          value: company.id,
          label: company.name,
          name: company.name,
          id: company.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setIndividualCompanies(formattedCompanies);
    } catch (error) {
      console.error('Error fetching individual companies:', error);
    }
  };

  // Fetch locations from API
  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000);
      const locationsData = response?.data?.locations || response?.data || [];
      
      const formattedLocations = locationsData
        .map(location => ({
          value: location.id,
          label: `${location.short}`, // Show short code and name
          short: location.short // Store the short code for filtering
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setLocationOptions(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Add filter function
  const applyFilters = useCallback(() => {
    let filtered = [...allTalents];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(talent => 
        (talent.name || talent.Name || '').toLowerCase().includes(searchLower) ||
        (talent.email || talent.E_Mail || '').toLowerCase().includes(searchLower) ||
        (talent.notes || talent.Notiz_Setcast || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply function filter
    if (selectedFunction) {
      filtered = filtered.filter(talent => 
        (talent.function || talent.Funktion) === selectedFunction
      );
    }

    // Apply type filter
    if (selectedType) {
      filtered = filtered.filter(talent => 
        (talent.type || talent.bereich || talent.Bereich) === selectedType
      );
    }

    // Apply company filters
    if (filterGoodGroups.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.goodPerformanceCompany || [];
        return filterGoodGroups.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    if (filterBadGroups.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.badPerformanceCompany || [];
        return filterBadGroups.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    if (filtertryoutGroups.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.tryOutCompany || talent.tryout_company || [];
        return filtertryoutGroups.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    // Apply location filter with improved logic
    if (filterLocation.length > 0) {
      filtered = filtered.filter(talent => {
        // Get talent locations, handling different possible data structures
        const talentLocations = Array.isArray(talent.locations) ? talent.locations : 
                              Array.isArray(talent.location) ? talent.location :
                              talent.location ? [talent.location] : [];

        // Check if any of the selected locations match the talent's locations
        return filterLocation.some(selectedLocation => {
          // Get the short code from the selected location option
          const selectedLocationShort = selectedLocation.short;
          
          return talentLocations.some(talentLocation => {
            // Talent locations are stored as short codes (strings)
            if (typeof talentLocation === 'string') {
              return talentLocation === selectedLocationShort;
            } else if (typeof talentLocation === 'object') {
              // Handle object format - compare with short, label, or name
              return talentLocation.short === selectedLocationShort ||
                     talentLocation.label === selectedLocationShort ||
                     talentLocation.name === selectedLocationShort;
            }
            return false;
          });
        });
      });
    }

    setTalents(filtered);
  }, [allTalents, search, selectedFunction, selectedType, filterGoodGroups, filterBadGroups, filtertryoutGroups, filterLocation]);

  // Update filter handlers to use client-side filtering
  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleTypeChange = useCallback((option) => {
    setSelectedType(option ? option.value : "");
  }, []);

  const handleFunctionChange = useCallback((option) => {
    setSelectedFunction(option ? option.value : "");
  }, []);

  // Apply filters whenever any filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Initialize data
  useEffect(() => {
    fetchTalents();
    fetchCompanies();
    fetchIndividualCompanies();
    fetchLocations();
  }, []);
  
  // Effect to expand all rows by default
  useEffect(() => {
    const allTalentIds = talents.map(talent => talent.id);
    setExpandedRows(new Set(allTalentIds)); // Start with all rows expanded
    setIsExpanded(true);
  }, [talents]);

  // Toggle expanding/collapsing all rows
  const toggleAllRows = () => {
    if (isExpanded) {
      setExpandedRows(new Set());
    } else {
      const allTalentIds = talents.map(talent => talent.id);
      setExpandedRows(new Set(allTalentIds));
    }
    setIsExpanded(!isExpanded);
  };

  // Toggle individual row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      
      // Update isExpanded state based on whether all rows are expanded
      const allTalentIds = talents.map(talent => talent.id);
      const allExpanded = allTalentIds.every(talentId => newSet.has(talentId));
      setIsExpanded(allExpanded);
      
      return newSet;
    });
  };

  // Function options
  const functionOptions = Array.from(new Set(talents.map(talent => 
    talent.function || talent.Funktion)
  ))
  .filter(Boolean)
  .map(func => ({
    value: func,
    label: func
  }));

  // Type options
  const typeOptions = Array.from(new Set(talents.map(talent => 
    talent.type || talent.bereich || talent.Bereich)
  ))
  .filter(Boolean)
  .map(type => ({
    value: type,
    label: type
  }));

  // Helper function to ensure company data has consistent format
  const ensureCompanyFormat = (companies) => {
    if (!companies || !Array.isArray(companies)) return [];
    
    return companies.map(company => {
      if (typeof company === 'string') {
        return {
          label: company,
          value: company,
          timestamp: new Date().toISOString()
        };
      } else if (typeof company === 'object') {
        return {
          label: company.label || company.name || '',
          value: company.value || company.id || '',
          rating: company.rating || undefined, // Preserve rating
          timestamp: company.timestamp || new Date().toISOString()
        };
      }
      return company;
    });
  };

  // Function to update a single talent in the local state (optimized to avoid full page reloads)
  const updateSingleTalent = useCallback((talentId, updatedData) => {
    setAllTalents(prevTalents => {
      return prevTalents.map(talent => {
        if (talent.id === talentId) {
          return { ...talent, ...updatedData };
        }
        return talent;
      });
    });
  }, []);

  // Helper function to get all individual companies from company groups and main companies list for selection
  const getAllIndividualCompanies = useCallback(() => {
    const allCompanies = [];
    
    // Add companies from company groups
    companyOptions.forEach(group => {
      if (group.companies && Array.isArray(group.companies)) {
        group.companies.forEach(company => {
          allCompanies.push({
            value: company.value,
            label: `${company.label} (${group.label})`, // Show company name and group
            groupName: group.label,
            groupId: group.value,
            originalCompany: company,
            source: 'group'
          });
        });
      }
    });
    
    // Add companies from main companies list
    individualCompanies.forEach(company => {
      // Check if this company is not already added from groups
      const existsInGroups = allCompanies.some(existing => 
        existing.originalCompany?.value === company.value || 
        existing.originalCompany?.label === company.label
      );
      
      if (!existsInGroups) {
        allCompanies.push({
          value: company.value,
          label: `${company.label} (Individual)`, // Mark as individual company
          groupName: 'Individual Companies',
          groupId: 'individual',
          originalCompany: {
            label: company.label,
            value: company.value,
            name: company.name,
            id: company.id
          },
          source: 'individual'
        });
      }
    });
    
    return allCompanies.sort((a, b) => a.label.localeCompare(b.label));
  }, [companyOptions, individualCompanies]);

  // Helper function to convert groups to individual companies for editing
  const convertGroupsToIndividualCompanies = (companies, companyOptionsToUse = companyOptions) => {
    if (!companies || !Array.isArray(companies)) return [];
    
    const individualCompanies = [];
    
    companies.forEach(company => {
      // If this company has group metadata, we need to find all individual companies from that group
      if (company.groupId && company.groupName) {
        // Find the group in our options
        const group = companyOptionsToUse.find(opt => opt.value === company.groupId);
        if (group && group.companies) {
          // Add all companies from this group with the company's rating
          group.companies.forEach(groupCompany => {
            individualCompanies.push({
              label: groupCompany.label,
              value: groupCompany.value,
              rating: company.rating || undefined,
              timestamp: company.timestamp || new Date().toISOString(),
              groupName: company.groupName,
              groupId: company.groupId
            });
          });
        }
      } else {
        // This is already an individual company
        individualCompanies.push({
          label: company.label || company.name || '',
          value: company.value || company.id || '',
          rating: company.rating || undefined,
          timestamp: company.timestamp || new Date().toISOString()
        });
      }
    });
    
    return individualCompanies;
  };

  // Helper function to add individual companies to performance categories
  const addIndividualCompanies = (performanceType, newCompanies) => {
    const newCompaniesWithMetadata = newCompanies.map(company => ({
      label: company.originalCompany.label || company.originalCompany.name,
      value: company.originalCompany.value || company.originalCompany.id,
      groupName: company.groupName,
      groupId: company.groupId,
      timestamp: new Date().toISOString(),
      rating: performanceType === 'good' ? 1 : undefined, // Default rating for good performance
      source: company.source // Track whether it came from group or individual list
    }));

    if (performanceType === 'good') {
      const updatedCompanies = [...selectedGoodCompanies, ...newCompaniesWithMetadata];
      setSelectedGoodCompanies(updatedCompanies);
    } else if (performanceType === 'bad') {
      const updatedCompanies = [...selectedBadCompanies, ...newCompaniesWithMetadata];
      setSelectedBadCompanies(updatedCompanies);
    } else if (performanceType === 'tryout') {
      const updatedCompanies = [...selectedtryoutCompanies, ...newCompaniesWithMetadata];
      setSelectedtryoutCompanies(updatedCompanies);
    }
  };

  // Edit talent function
  const editTalent = async (talent) => {
    // console.log('DEBUG - Starting edit for talent:', talent);
    
    // Ensure company options are loaded before proceeding
    let currentCompanyOptions = companyOptions;
    if (!currentCompanyOptions || currentCompanyOptions.length === 0) {
      // console.log('DEBUG - Company options not loaded, fetching them first...');
      try {
        const response = await companyGroupService.getAllGroups(1, 1000);
        const groupsData = response?.data?.data?.groups || response?.data || [];
        
        currentCompanyOptions = groupsData
          .map(group => ({
            value: group.id,
            label: group.name,
            companies: group.companies // Store the companies for later use
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        // Update state as well
        setCompanyOptions(currentCompanyOptions);
        // console.log('DEBUG - Loaded company options:', currentCompanyOptions);
      } catch (error) {
        console.error('DEBUG - Error fetching company options:', error);
        currentCompanyOptions = [];
      }
    }
    
    setIsEditing(true);
    setCurrentTalent(talent);
    setCurrentEmail(talent.email || talent.E_Mail);
    setCurrentName(talent.name || talent.Name);
    setCurrentFunction(talent.function || talent.Funktion);
    setCurrentSurname(talent.notes || talent.Notiz_Setcast);
    setCurrentPhone(talent.phone || talent.Telefon);
    setCurrentPrice(talent.rate || talent.Tagessatz);
    
    // Set notes
    setNotesProductLeads(talent.notesProductLeads || '');
    setNotesProductLeadsdd(talent.notesProductLeadsdd || null);
    
    try {
      // Fetch company relationships from new endpoint
      const response = await talentService.getTalentCompanies(talent.id);
      // console.log('DEBUG - Fetched company relationships:', response);
      
      // Check the actual response structure - data is directly in response.data
      if (response && response.data && (response.data.goodPerformanceCompany || response.data.badPerformanceCompany || response.data.tryOutCompany)) {
        const { goodPerformanceCompany, badPerformanceCompany, tryOutCompany } = response.data;
        
        // console.log('DEBUG - API returned good companies:', goodPerformanceCompany);
        // console.log('DEBUG - API returned bad companies:', badPerformanceCompany);
        // console.log('DEBUG - API returned tryout companies:', tryOutCompany);
        // console.log('DEBUG - Current company options available:', currentCompanyOptions);
        
        // Helper function to convert companies back to group selections using the loaded options
        const convertCompaniesToGroups = (companies) => {
          // console.log('DEBUG - Converting companies to groups, input:', companies);
          if (!companies || !Array.isArray(companies)) {
            // console.log('DEBUG - No companies to convert or not an array');
            return [];
          }
          
          const groupMap = new Map();
          
          companies.forEach(company => {
            if (company.groupId && company.groupName) {
              // This company belongs to a group (has metadata)
              if (!groupMap.has(company.groupId)) {
                groupMap.set(company.groupId, {
                  value: company.groupId,
                  label: company.groupName,
                  rating: company.rating || 1,
                  ratingLabel: `Rating ${company.rating || 1}`
                });
              }
            } else {
              // Try to find the group by matching company with currentCompanyOptions
              const companyValue = company.value || company.label;
              const companyLabel = company.label || company.name;
              
              // console.log('DEBUG - Looking for group for company:', companyLabel, companyValue);
              
              currentCompanyOptions.forEach(group => {
                if (group.companies && Array.isArray(group.companies)) {
                  const matchingCompany = group.companies.find(groupCompany => 
                    groupCompany.value === companyValue || 
                    groupCompany.label === companyLabel ||
                    groupCompany.value === companyLabel || 
                    groupCompany.label === companyValue
                  );
                  
                  if (matchingCompany && !groupMap.has(group.value)) {
                    groupMap.set(group.value, {
                      value: group.value,
                      label: group.label,
                      rating: company.rating || 1,
                      ratingLabel: `Rating ${company.rating || 1}`
                    });
                    // console.log('DEBUG - Found group for company:', companyLabel, 'in group:', group.label, 'with rating:', company.rating || 1);
                  }
                }
              });
            }
          });
          
          const result = Array.from(groupMap.values());
          // console.log('DEBUG - Converted companies to groups:', result);
          return result;
        };
        
        // Convert retrieved company data to group selections
        const formattedGoodGroups = convertCompaniesToGroups(goodPerformanceCompany);
        const formattedBadGroups = convertCompaniesToGroups(badPerformanceCompany);
        const formattedTryoutGroups = convertCompaniesToGroups(tryOutCompany);
        
        // console.log('DEBUG - Formatted good groups for select:', formattedGoodGroups);
        // console.log('DEBUG - Formatted bad groups for select:', formattedBadGroups);
        // console.log('DEBUG - Formatted tryout groups for select:', formattedTryoutGroups);
        
        setSelectedGoodGroups(formattedGoodGroups);
        setSelectedBadGroups(formattedBadGroups);
        setSelectedtryoutGroups(formattedTryoutGroups);
        
        // Also populate individual company arrays for editing
        const individualGoodCompanies = convertGroupsToIndividualCompanies(goodPerformanceCompany, currentCompanyOptions);
        const individualBadCompanies = convertGroupsToIndividualCompanies(badPerformanceCompany, currentCompanyOptions);
        const individualTryoutCompanies = convertGroupsToIndividualCompanies(tryOutCompany, currentCompanyOptions);
        
        setSelectedGoodCompanies(individualGoodCompanies);
        setSelectedBadCompanies(individualBadCompanies);
        setSelectedtryoutCompanies(individualTryoutCompanies);
      } else {
        // console.log('DEBUG - No company data found, setting empty arrays');
        setSelectedGoodGroups([]);
        setSelectedBadGroups([]);
        setSelectedtryoutGroups([]);
        setSelectedGoodCompanies([]);
        setSelectedBadCompanies([]);
        setSelectedtryoutCompanies([]);
      }
    } catch (error) {
      console.error('DEBUG - Error fetching company relationships:', error);
      
      // Fallback: try to reconstruct groups from talent object data
      // console.log('DEBUG - Attempting fallback group reconstruction from talent object');
      
      const fallbackConvertCompaniesToGroups = (companies) => {
        if (!companies || !Array.isArray(companies)) return [];
        
        const groupMap = new Map();
        
        companies.forEach(company => {
          // Handle both string and object company formats
          const companyValue = typeof company === 'string' ? company : (company.value || company.label);
          const companyLabel = typeof company === 'string' ? company : (company.label || company.name);
          const companyRating = typeof company === 'object' ? (company.rating || 1) : 1;
          
          // console.log('DEBUG - Fallback: Looking for group for company:', companyLabel, companyValue);
          
          // Try to find the group by matching company with currentCompanyOptions
          currentCompanyOptions.forEach(group => {
            if (group.companies && Array.isArray(group.companies)) {
              const matchingCompany = group.companies.find(groupCompany => 
                groupCompany.value === companyValue || 
                groupCompany.label === companyLabel ||
                groupCompany.value === companyLabel || 
                groupCompany.label === companyValue
              );
              
              if (matchingCompany && !groupMap.has(group.value)) {
                groupMap.set(group.value, {
                  value: group.value,
                  label: group.label,
                  rating: companyRating,
                  ratingLabel: `Rating ${companyRating}`
                });
                // console.log('DEBUG - Fallback: Found group for company:', companyLabel, 'in group:', group.label, 'with rating:', companyRating);
              }
            }
          });
        });
        
        const result = Array.from(groupMap.values());
        // console.log('DEBUG - Fallback: Converted companies to groups:', result);
        return result;
      };
      
      const fallbackGoodGroups = fallbackConvertCompaniesToGroups(talent.goodPerformanceCompany || []);
      const fallbackBadGroups = fallbackConvertCompaniesToGroups(talent.badPerformanceCompany || []);
      const fallbackTryoutGroups = fallbackConvertCompaniesToGroups(talent.tryOutCompany || []);
      
      // console.log('DEBUG - Fallback good groups:', fallbackGoodGroups);
      // console.log('DEBUG - Fallback bad groups:', fallbackBadGroups);
      // console.log('DEBUG - Fallback tryout groups:', fallbackTryoutGroups);
      
      setSelectedGoodGroups(fallbackGoodGroups);
      setSelectedBadGroups(fallbackBadGroups);
      setSelectedtryoutGroups(fallbackTryoutGroups);
      
      // Also populate individual company arrays from fallback data
      const fallbackIndividualGood = convertGroupsToIndividualCompanies(talent.goodPerformanceCompany || [], currentCompanyOptions);
      const fallbackIndividualBad = convertGroupsToIndividualCompanies(talent.badPerformanceCompany || [], currentCompanyOptions);
      const fallbackIndividualTryout = convertGroupsToIndividualCompanies(talent.tryOutCompany || [], currentCompanyOptions);
      
      setSelectedGoodCompanies(fallbackIndividualGood);
      setSelectedBadCompanies(fallbackIndividualBad);
      setSelectedtryoutCompanies(fallbackIndividualTryout);
    }
  };

  // Helper function to expand company groups into individual companies with ratings
  const expandGroupsToCompanies = (groups, performanceType, companyOptionsToUse = companyOptions) => {
    const expandedCompanies = [];
    
    groups.forEach(group => {
      // Find the full group data from companyOptionsToUse
      const fullGroupData = companyOptionsToUse.find(option => option.value === group.value);
      
      if (fullGroupData && fullGroupData.companies) {
        // Add each company from the group with the group's rating and group metadata
        fullGroupData.companies.forEach(company => {
          expandedCompanies.push({
            label: company.label,
            value: company.value,
            rating: group.rating || 1,
            timestamp: new Date().toISOString(),
            groupName: fullGroupData.label,
            groupId: fullGroupData.value
          });
        });
      }
    });
    
    return expandedCompanies;
  };

  // Component for adding individual companies
  const IndividualCompanySelector = ({ 
    selectedCompanies, 
    onCompaniesChange, 
    performanceType,
    placeholder 
  }) => {
    const [tempCompanies, setTempCompanies] = useState([]);
    const availableCompanies = getAllIndividualCompanies();
    
    // Filter out companies that are already selected
    const availableOptions = availableCompanies.filter(company => 
      !selectedCompanies.some(selected => selected.value === company.value)
    );

    const addSelectedCompanies = () => {
      if (tempCompanies.length > 0) {
        addIndividualCompanies(performanceType, tempCompanies);
        setTempCompanies([]);
      }
    };

    return (
      <div style={{ 
        marginBottom: '10px',
        padding: '8px',
        backgroundColor: '#fff',
        borderRadius: '0px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ 
          fontSize: '0.8em',
          fontWeight: 'bold',
          marginBottom: '8px',
          color: '#495057'
        }}>
          Add Individual Companies:
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Select
            isMulti
            value={tempCompanies}
            onChange={setTempCompanies}
            options={availableOptions}
            placeholder={placeholder}
            styles={customStyles}
            isClearable
          />
          <button 
            type="button"
            className="button"
            onClick={addSelectedCompanies}
            disabled={tempCompanies.length === 0}
            style={{ 
              padding: '8px 12px',
              fontSize: '10px',
              minWidth: 'auto',
              minHeight: "38px",
            }}
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  // Helper function to create a group selector with rating
  const GroupWithRatingSelector = ({ 
    selectedGroups, 
    onGroupsChange, 
    placeholder, 
    performanceType 
  }) => {
    const [tempGroup, setTempGroup] = useState(null);
    const [tempRating, setTempRating] = useState(ratingOptions[0]);

    const addGroupWithRating = () => {
      if (tempGroup && tempRating) {
        const newGroup = {
          ...tempGroup,
          rating: tempRating.value,
          ratingLabel: tempRating.label
        };
        
        // Check if group already exists
        const existingGroupIndex = selectedGroups.findIndex(g => g.value === newGroup.value);
        
        let updatedGroups;
        if (existingGroupIndex >= 0) {
          // Update existing group's rating
          updatedGroups = [...selectedGroups];
          updatedGroups[existingGroupIndex] = newGroup;
        } else {
          // Add new group
          updatedGroups = [...selectedGroups, newGroup];
        }
        
        onGroupsChange(updatedGroups);
        
        // Also update individual companies for this performance type
        if (performanceType === 'good') {
          const individualCompanies = convertGroupsToIndividualCompanies(
            updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
          );
          setSelectedGoodCompanies(individualCompanies);
        } else if (performanceType === 'bad') {
          const individualCompanies = convertGroupsToIndividualCompanies(
            updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
          );
          setSelectedBadCompanies(individualCompanies);
        } else if (performanceType === 'tryout') {
          const individualCompanies = convertGroupsToIndividualCompanies(
            updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
          );
          setSelectedtryoutCompanies(individualCompanies);
        }
        
        // Reset temp selections
        setTempGroup(null);
        setTempRating(ratingOptions[0]);
      }
    };

    const removeGroup = (groupToRemove) => {
      const updatedGroups = selectedGroups.filter(g => g.value !== groupToRemove.value);
      onGroupsChange(updatedGroups);
      
      // Also update individual companies for this performance type
      if (performanceType === 'good') {
        const individualCompanies = convertGroupsToIndividualCompanies(
          updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
        );
        setSelectedGoodCompanies(individualCompanies);
      } else if (performanceType === 'bad') {
        const individualCompanies = convertGroupsToIndividualCompanies(
          updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
        );
        setSelectedBadCompanies(individualCompanies);
      } else if (performanceType === 'tryout') {
        const individualCompanies = convertGroupsToIndividualCompanies(
          updatedGroups.map(g => ({ ...g, groupId: g.value, groupName: g.label }))
        );
        setSelectedtryoutCompanies(individualCompanies);
      }
    };

    return (
      <div>
        {/* Show current selections header if there are any */}
        {selectedGroups && selectedGroups.length > 0 && (
          <div style={{ 
            marginBottom: '10px',
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '0px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ 
              fontSize: '0.8em',
              fontWeight: 'bold',
              marginBottom: '5px',
              color: '#495057'
            }}>
              Currently Selected ({selectedGroups.length} group{selectedGroups.length !== 1 ? 's' : ''}):
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {selectedGroups.map((group, index) => (
                <span 
                  key={index}
                  style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 8px',
                    backgroundColor: performanceType === 'good' ? '#d4edda' : 
                                     performanceType === 'bad' ? '#f8d7da' : '#e2e3e5',
                    border: `1px solid ${performanceType === 'good' ? '#c3e6cb' : 
                                        performanceType === 'bad' ? '#f5c6cb' : '#d6d8db'}`,
                    borderRadius: '0px',
                    fontSize: '0.8em'
                  }}
                >
                  <span>{group.label}</span>
                  {performanceType === 'good' && group.rating && (
                    <span style={{ 
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      color: '#495057'
                    }}>
                      (Rating {group.rating})
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeGroup(group)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Add new group section */}
        <div style={{ 
          marginBottom: '10px',
          padding: '8px',
          backgroundColor: '#fff',
          borderRadius: '0px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ 
            fontSize: '0.8em',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: '#495057'
          }}>
            Add New Group:
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Select
              value={tempGroup}
              onChange={setTempGroup}
              options={companyOptions}
              placeholder={placeholder}
              styles={customStyles}
              isClearable
            />
            {performanceType === 'good' && (
              <Select
                value={tempRating}
                onChange={setTempRating}
                options={ratingOptions}
                placeholder="Select Rating"
                styles={customStyles}
              />
            )}
            <button 
              type="button"
              className="button" style={{ padding: '8px 12px', fontSize: '10px', minWidth: 'auto', minHeight: '38px' }}
              onClick={addGroupWithRating}
              disabled={!tempGroup}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Update talent details function
  const updateTalentDetails = async () => {
    if (currentTalent) {
      try {
        // Ensure we have current company options
        let currentCompanyOptions = companyOptions;
        if (!currentCompanyOptions || currentCompanyOptions.length === 0) {
          // console.log('DEBUG - Company options not loaded for update, fetching them...');
          try {
            const response = await companyGroupService.getAllGroups(1, 1000);
            const groupsData = response?.data?.data?.groups || response?.data || [];
            
            currentCompanyOptions = groupsData
              .map(group => ({
                value: group.id,
                label: group.name,
                companies: group.companies // Store the companies for later use
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
            
            // Update state as well
            setCompanyOptions(currentCompanyOptions);
          } catch (error) {
            console.error('DEBUG - Error fetching company options for update:', error);
            currentCompanyOptions = [];
          }
        }
        
        // console.log('Current good groups before update:', selectedGoodGroups);
        // console.log('Current bad groups before update:', selectedBadGroups);
        // console.log('Current tryout groups before update:', selectedtryoutGroups);
        
        // Get the original talent data first to compare changes
        const originalTalentResponse = await talentService.getTalentById(currentTalent.id);
        const originalTalent = originalTalentResponse.data;
        
        // Compare basic info fields and only include changed ones
        const changedBasicInfo = {};
        
        // Helper function to check if a field has changed
        const hasFieldChanged = (newValue, oldValue, fieldName) => {
          // console.log(`Checking ${fieldName}: new="${newValue}" vs old="${oldValue}"`);
          return newValue !== oldValue;
        };
        
        // Check each field for changes
        if (hasFieldChanged(notesProductLeads, originalTalent.productLeadNotes || originalTalent.Product_Lead_Notes || originalTalent.notesProductLeads || '', 'notesProductLeads')) {
          changedBasicInfo.notesProductLeads = notesProductLeads;
        }
        
        // Handle Select component value comparison for notes type
        const originalNotesType = originalTalent.productLeadNotesType || originalTalent.Product_Lead_Notes_Type || originalTalent.notesProductLeadsdd;
        const currentNotesTypeValue = notesProductLeadsdd?.value || notesProductLeadsdd;
        const originalNotesTypeValue = originalNotesType?.value || originalNotesType;
        
        if (hasFieldChanged(currentNotesTypeValue, originalNotesTypeValue, 'notesProductLeadsdd')) {
          changedBasicInfo.notesProductLeadsdd = notesProductLeadsdd;
        }
        
        // Filter out undefined values before sending to API
        const cleanedBasicInfo = {};
        Object.keys(changedBasicInfo).forEach(key => {
          if (changedBasicInfo[key] !== undefined && changedBasicInfo[key] !== null) {
            cleanedBasicInfo[key] = changedBasicInfo[key];
          }
        });

        // Only update basic info if there are changes
        if (Object.keys(cleanedBasicInfo).length > 0) {
          // console.log('Debug - SENDING TO API:', cleanedBasicInfo);
          await talentService.updateTalent(currentTalent.id, cleanedBasicInfo);
        } else {
          // console.log('Debug - NO CHANGES to basic info, not sending API request');
        }
        
        // Get the original companies from the API first
        const originalResponse = await talentService.getTalentCompanies(currentTalent.id);
        
        if (originalResponse && originalResponse.data && (originalResponse.data.goodPerformanceCompany || originalResponse.data.badPerformanceCompany || originalResponse.data.tryOutCompany)) {
          const originalData = originalResponse.data;
          
          // Get original companies for each category
          const originalGoodCompanies = originalData.goodPerformanceCompany || [];
          const originalBadCompanies = originalData.badPerformanceCompany || [];
          const originalTryoutCompanies = originalData.tryOutCompany || [];
          
          // console.log('Original good companies from API:', originalGoodCompanies);
          // console.log('Original bad companies from API:', originalBadCompanies);
          // console.log('Original tryout companies from API:', originalTryoutCompanies);
          
          // Helper function to check if individual companies have actually changed
          const hasCompanyResultChanged = (selectedGroups, selectedIndividualCompanies, originalCompanies, categoryName) => {
            // Step 1: Get all individual companies that should be in the final result
            // This includes companies from group expansions + standalone individual companies
            const expandedFromGroups = expandGroupsToCompanies(selectedGroups, 'comparison', currentCompanyOptions);
            
            // Combine all companies (groups expanded to individuals + standalone individuals)
            const allSelectedCompanies = [...expandedFromGroups];
            
            // Add individual companies that aren't already included from groups
            const groupCompanyValues = new Set(expandedFromGroups.map(c => c.value));
            selectedIndividualCompanies.forEach(individual => {
              if (!groupCompanyValues.has(individual.value)) {
                allSelectedCompanies.push(individual);
              }
            });
            
            // Step 2: Compare individual companies only (ignore group structure)
            const currentCompanies = allSelectedCompanies.map(c => ({ 
              value: String(c.value), 
              rating: c.rating || 1 
            })).sort((a, b) => a.value.localeCompare(b.value));
            
            const originalCompaniesFormatted = originalCompanies.map(c => ({ 
              value: String(c.value), 
              rating: c.rating || 1 
            })).sort((a, b) => a.value.localeCompare(b.value));
            
            // console.log(`${categoryName} individual company comparison:`, {
              // currentCount: currentCompanies.length,
              // originalCount: originalCompaniesFormatted.length,
              // currentCompanies: currentCompanies,
              // originalCompanies: originalCompaniesFormatted
            // });
            
            // Check if company lists are different
            if (currentCompanies.length !== originalCompaniesFormatted.length) {
              // console.log(`${categoryName} individual company count changed: ${originalCompaniesFormatted.length} -> ${currentCompanies.length}`);
              return true;
            }
            
            // Check if any individual company or rating changed
            const hasChanges = !currentCompanies.every(current => {
              const original = originalCompaniesFormatted.find(orig => orig.value === current.value);
              const exists = !!original;
              const ratingMatch = exists && original.rating === current.rating;
              
              if (!exists) {
                // console.log(`${categoryName} - New company: ${current.value}`);
              } else if (!ratingMatch) {
                // console.log(`${categoryName} - Rating changed for ${current.value}: ${original.rating} -> ${current.rating}`);
              }
              
              return exists && ratingMatch;
            });
            
            if (hasChanges) {
              // console.log(`${categoryName} individual companies changed`);
            } else {
              // console.log(`${categoryName} individual companies unchanged`);
            }
            
            return hasChanges;
          };
          
          // Check which categories have actually changed by comparing final results
          const goodChanged = hasCompanyResultChanged(selectedGoodGroups, selectedGoodCompanies, originalGoodCompanies, 'good');
          const badChanged = hasCompanyResultChanged(selectedBadGroups, selectedBadCompanies, originalBadCompanies, 'bad');
          const tryoutChanged = hasCompanyResultChanged(selectedtryoutGroups, selectedtryoutCompanies, originalTryoutCompanies, 'tryout');
          
          // console.log('Company result changes:', { goodChanged, badChanged, tryoutChanged });
          
          // Helper function to update companies considering both group changes and individual deletions
          const updateCategoryCompanies = (selectedGroups, originalCompanies, selectedIndividualCompanies) => {
            // console.log('=== CATEGORY UPDATE DEBUG ===');
            // console.log('Selected groups:', selectedGroups.map(g => ({ label: g.label, value: g.value, rating: g.rating })));
            // console.log('Original companies:', originalCompanies.map(c => ({ label: c.label, value: c.value, rating: c.rating, timestamp: c.timestamp })));
            // console.log('Selected individual companies:', selectedIndividualCompanies.map(c => ({ label: c.label, value: c.value, rating: c.rating })));
            
            // Step 1: Expand selected groups to individual companies
            const expandedCompanies = expandGroupsToCompanies(selectedGroups, 'update', currentCompanyOptions);
            // console.log('Expanded companies from groups:', expandedCompanies.map(c => ({ label: c.label, value: c.value, rating: c.rating })));
            
            // Step 2: Create a set of companies that should remain (from groups)
            const groupCompanyValues = new Set(expandedCompanies.map(c => c.value));
            
            // Step 3: Create a set of companies that were individually selected to keep
            const individualCompanyValues = new Set(selectedIndividualCompanies.map(c => c.value));
            
            // Step 4: Final list should include:
            // - Companies from groups that weren't individually deleted
            // - Original companies that are still in the individual selection
            
            const finalCompanies = [];
            
            // Add companies from group expansions (new or existing)
            expandedCompanies.forEach(expandedCompany => {
              // Check if this company was individually deleted
              if (individualCompanyValues.has(expandedCompany.value)) {
                // Find original data to preserve timestamps and detect actual changes
                const originalCompany = originalCompanies.find(orig => orig.value === expandedCompany.value);
                const newRating = expandedCompany.rating || 1;
                const originalRating = originalCompany?.rating || 1;
                
                // Only update timestamp if this is a new company or rating changed
                const isNewCompany = !originalCompany;
                const ratingChanged = originalRating !== newRating;
                const shouldUpdateTimestamp = isNewCompany || ratingChanged;
                
                // console.log(`Processing company ${expandedCompany.label} (${expandedCompany.value}):`);
                // console.log(`  - Original exists: ${!!originalCompany}`);
                // console.log(`  - Original rating: ${originalRating}, New rating: ${newRating}`);
                // console.log(`  - Rating changed: ${ratingChanged}`);
                // console.log(`  - Will update timestamp: ${shouldUpdateTimestamp}`);
                // console.log(`  - Original timestamp: ${originalCompany?.timestamp}`);
                
                if (shouldUpdateTimestamp) {
                  // console.log(`⚠️ UPDATING timestamp for ${expandedCompany.label}: ${isNewCompany ? 'new company' : `rating changed from ${originalRating} to ${newRating}`}`);
                } else {
                  // console.log(`✓ PRESERVING timestamp for ${expandedCompany.label}`);
                }
                
                finalCompanies.push({
                  label: expandedCompany.label,
                  value: expandedCompany.value,
                  rating: newRating,
                  timestamp: shouldUpdateTimestamp ? new Date().toISOString() : (originalCompany?.timestamp || new Date().toISOString()),
                  groupName: expandedCompany.groupName,
                  groupId: expandedCompany.groupId
                });
              }
            });
            
            // Add any remaining individual companies that don't come from current groups
            selectedIndividualCompanies.forEach(individualCompany => {
              if (!groupCompanyValues.has(individualCompany.value)) {
                // Find if this company existed before to preserve timestamp
                const originalCompany = originalCompanies.find(orig => orig.value === individualCompany.value);
                const newRating = individualCompany.rating || 1;
                const originalRating = originalCompany?.rating || 1;
                
                // Only update timestamp if this is a new company or rating changed
                const isNewCompany = !originalCompany;
                const ratingChanged = originalRating !== newRating;
                const shouldUpdateTimestamp = isNewCompany || ratingChanged;
                
                if (shouldUpdateTimestamp) {
                  // console.log(`Updating timestamp for individual ${individualCompany.label}: ${isNewCompany ? 'new company' : `rating changed from ${originalRating} to ${newRating}`}`);
                }
                
                finalCompanies.push({
                  label: individualCompany.label,
                  value: individualCompany.value,
                  rating: newRating,
                  timestamp: shouldUpdateTimestamp ? new Date().toISOString() : (originalCompany?.timestamp || new Date().toISOString()),
                  groupName: individualCompany.groupName,
                  groupId: individualCompany.groupId
                });
              }
            });
            
            // console.log('Final companies for category:', finalCompanies);
            return finalCompanies;
          };
          
          // Helper function to get current companies for a category (expand groups + add individuals)
          const getCurrentCompaniesForCategory = (selectedGroups, selectedIndividualCompanies) => {
            const expandedFromGroups = expandGroupsToCompanies(selectedGroups, 'current', currentCompanyOptions);
            const allCompanies = [...expandedFromGroups];
            
            // Add individual companies that aren't already included from groups
            const groupCompanyValues = new Set(expandedFromGroups.map(c => c.value));
            selectedIndividualCompanies.forEach(individual => {
              if (!groupCompanyValues.has(individual.value)) {
                allCompanies.push(individual);
              }
            });
            
            return allCompanies;
          };

          // Helper function to identify only the companies that actually changed
          const getOnlyChangedCompanies = (currentCompanies, originalCompanies, categoryName) => {
            const result = {
              hasChanges: false,
              updatedCompanies: []
            };
            
            // console.log(`=== ANALYZING CHANGES FOR ${categoryName.toUpperCase()} ===`);
            
            // Build a map of original companies for quick lookup
            const originalMap = new Map();
            originalCompanies.forEach(orig => {
              originalMap.set(String(orig.value), {
                rating: orig.rating || 1,
                timestamp: orig.timestamp,
                label: orig.label
              });
            });
            
            // Check each current company
            currentCompanies.forEach(current => {
              const currentValue = String(current.value);
              const currentRating = current.rating || 1;
              const original = originalMap.get(currentValue);
              
              if (!original) {
                // This is a new company
                // console.log(`🆕 NEW company: ${current.label} (${currentValue})`);
                result.updatedCompanies.push({
                  label: current.label,
                  value: current.value,
                  rating: currentRating,
                  timestamp: new Date().toISOString(),
                  groupName: current.groupName,
                  groupId: current.groupId
                });
                result.hasChanges = true;
              } else if (original.rating !== currentRating) {
                // Rating changed
                // console.log(`⭐ RATING changed: ${current.label} (${original.rating} → ${currentRating})`);
                result.updatedCompanies.push({
                  label: current.label,
                  value: current.value,
                  rating: currentRating,
                  timestamp: new Date().toISOString(),
                  groupName: current.groupName,
                  groupId: current.groupId
                });
                result.hasChanges = true;
              } else {
                // No change - preserve original data
                // console.log(`✓ UNCHANGED: ${current.label} (keeping original timestamp)`);
                result.updatedCompanies.push({
                  label: current.label,
                  value: current.value,
                  rating: currentRating,
                  timestamp: original.timestamp,
                  groupName: current.groupName,
                  groupId: current.groupId
                });
              }
            });
            
            // Check for removed companies
            originalCompanies.forEach(orig => {
              const stillExists = currentCompanies.some(current => String(current.value) === String(orig.value));
              if (!stillExists) {
                // console.log(`🗑️ REMOVED company: ${orig.label} (${orig.value})`);
                result.hasChanges = true;
              }
            });
            
            // console.log(`${categoryName} changes summary: ${result.hasChanges ? 'HAS CHANGES' : 'NO CHANGES'}`);
            return result;
          };

          // Prepare update data - only include categories that have changed
          const updatedCompanyData = {};
          
          // Instead of rebuilding entire categories, only send the actual changes
          if (goodChanged) {
            const currentGoodCompanies = getCurrentCompaniesForCategory(selectedGoodGroups, selectedGoodCompanies);
            const changesOnly = getOnlyChangedCompanies(currentGoodCompanies, originalGoodCompanies, 'good');
            if (changesOnly.hasChanges) {
              updatedCompanyData.goodPerformanceCompany = changesOnly.updatedCompanies;
              // console.log('Including good companies in update (changes only)');
            }
          }
          
          if (badChanged) {
            const currentBadCompanies = getCurrentCompaniesForCategory(selectedBadGroups, selectedBadCompanies);
            const changesOnly = getOnlyChangedCompanies(currentBadCompanies, originalBadCompanies, 'bad');
            if (changesOnly.hasChanges) {
              updatedCompanyData.badPerformanceCompany = changesOnly.updatedCompanies;
              // console.log('Including bad companies in update (changes only)');
            }
          }
          
          if (tryoutChanged) {
            const currentTryoutCompanies = getCurrentCompaniesForCategory(selectedtryoutGroups, selectedtryoutCompanies);
            const changesOnly = getOnlyChangedCompanies(currentTryoutCompanies, originalTryoutCompanies, 'tryout');
            if (changesOnly.hasChanges) {
              updatedCompanyData.tryOutCompany = changesOnly.updatedCompanies;
              // console.log('Including tryout companies in update (changes only)');
            }
          }
          
          // Only send update if there are actual changes
          if (Object.keys(updatedCompanyData).length > 0) {
            // console.log('Sending partial company update:', updatedCompanyData);
            
            // Send the partial update
            const updateResponse = await talentService.updateTalentCompanies(currentTalent.id, updatedCompanyData);
            // console.log('Company update response:', updateResponse);
          } else {
            // console.log('No company changes detected, skipping company update');
          }
        } else {
          // Fallback - only update if we have selections
          const updatedCompanyData = {};
          
          if (selectedGoodGroups.length > 0) {
            const expandedGoodCompanies = expandGroupsToCompanies(selectedGoodGroups, 'good', currentCompanyOptions);
            updatedCompanyData.goodPerformanceCompany = expandedGoodCompanies.map(company => ({
              label: company.label,
              value: company.value,
              rating: company.rating || 1,
              timestamp: new Date().toISOString(),
              groupName: company.groupName,
              groupId: company.groupId
            }));
          }
          
          if (selectedBadGroups.length > 0) {
            const expandedBadCompanies = expandGroupsToCompanies(selectedBadGroups, 'bad', currentCompanyOptions);
            updatedCompanyData.badPerformanceCompany = expandedBadCompanies.map(company => ({
              label: company.label,
              value: company.value,
              rating: company.rating || 1,
              timestamp: new Date().toISOString(),
              groupName: company.groupName,
              groupId: company.groupId
            }));
          }
          
          if (selectedtryoutGroups.length > 0) {
            const expandedTryoutCompanies = expandGroupsToCompanies(selectedtryoutGroups, 'tryout', currentCompanyOptions);
            updatedCompanyData.tryOutCompany = expandedTryoutCompanies.map(company => ({
              label: company.label,
              value: company.value,
              rating: company.rating || 1,
              timestamp: new Date().toISOString(),
              groupName: company.groupName,
              groupId: company.groupId
            }));
          }
          
          // Only send update if we have data
          if (Object.keys(updatedCompanyData).length > 0) {
            // console.log('Fallback: sending company data:', updatedCompanyData);
            const updateResponse = await talentService.updateTalentCompanies(currentTalent.id, updatedCompanyData);
            // console.log('Fallback company update response:', updateResponse);
          }
        }

        // Update the talent in local state instead of refetching all talents
        const updatedTalentData = {
          // Update basic info
          notesProductLeads: notesProductLeads,
          notesProductLeadsdd: notesProductLeadsdd,
          // Preserve existing core fields to prevent them from being cleared during update
          locations: currentTalent.locations,
          name: currentTalent.name || currentTalent.Name,
          email: currentTalent.email || currentTalent.E_Mail,
          phone: currentTalent.phone || currentTalent.Telefon,
          function: currentTalent.function || currentTalent.Funktion,
          type: currentTalent.type || currentTalent.bereich || currentTalent.Bereich,
          rate: currentTalent.rate || currentTalent.Tagessatz,
          notes: currentTalent.notes || currentTalent.Notiz_Setcast,
        };

        // Update company relationships from selected groups (fallback scenario)
        if (selectedGoodGroups.length > 0) {
          const expandedGoodCompanies = expandGroupsToCompanies(selectedGoodGroups, 'good', currentCompanyOptions);
          updatedTalentData.goodPerformanceCompany = expandedGoodCompanies;
        }
        
        if (selectedBadGroups.length > 0) {
          const expandedBadCompanies = expandGroupsToCompanies(selectedBadGroups, 'bad', currentCompanyOptions);
          updatedTalentData.badPerformanceCompany = expandedBadCompanies;
        }
        
        if (selectedtryoutGroups.length > 0) {
          const expandedTryoutCompanies = expandGroupsToCompanies(selectedtryoutGroups, 'tryout', currentCompanyOptions);
          updatedTalentData.tryOutCompany = expandedTryoutCompanies;
        }

        // Update the single talent in local state
        updateSingleTalent(currentTalent.id, updatedTalentData);
        
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating talent:', error);
        alert('Error updating talent. Please try again.');
      }
    }
  };

  // Helper function to group companies by their original group for display
  const groupCompaniesByGroup = (companies) => {
    if (!companies || !Array.isArray(companies)) return [];
    
    const grouped = {};
    const ungrouped = [];
    
    companies.forEach(company => {
      if (company.groupId && company.groupName) {
        // This company belongs to a group and has metadata
        if (!grouped[company.groupId]) {
          grouped[company.groupId] = {
            groupName: company.groupName,
            groupId: company.groupId,
            rating: company.rating,
            companies: [],
            timestamp: company.timestamp
          };
        }
        grouped[company.groupId].companies.push(company);
      } else {
        // Try to find the group by matching company with companyOptions
        let foundGroup = null;
        const companyValue = company.value || company.label;
        const companyLabel = company.label || company.name;
        
        companyOptions.forEach(group => {
          if (group.companies && Array.isArray(group.companies)) {
            const matchingCompany = group.companies.find(groupCompany => 
              groupCompany.value === companyValue || 
              groupCompany.label === companyLabel ||
              groupCompany.value === companyLabel || 
              groupCompany.label === companyValue
            );
            
            if (matchingCompany) {
              foundGroup = {
                groupId: group.value,
                groupName: group.label,
                rating: company.rating || 1,
                timestamp: company.timestamp
              };
            }
          }
        });
        
        if (foundGroup) {
          // Add to the found group
          if (!grouped[foundGroup.groupId]) {
            grouped[foundGroup.groupId] = {
              groupName: foundGroup.groupName,
              groupId: foundGroup.groupId,
              rating: foundGroup.rating,
              companies: [],
              timestamp: foundGroup.timestamp
            };
          }
          grouped[foundGroup.groupId].companies.push({
            ...company,
            groupId: foundGroup.groupId,
            groupName: foundGroup.groupName
          });
        } else {
          // This is truly an individual company
          ungrouped.push(company);
        }
      }
    });
    
    // Convert grouped object to array and combine with ungrouped
    const groupedArray = Object.values(grouped);
    return [...groupedArray, ...ungrouped];
  };

  // Helper function to render company display with proper grouping
  const renderCompanyDisplay = (companies, colorClass) => {
    if (!companies || !Array.isArray(companies)) return '';
    
    const groupedCompanies = groupCompaniesByGroup(companies);
    
    return groupedCompanies.map((item, index) => {
      if (item.groupName) {
        // This is a group
        return (
          <div key={`group-${item.groupId}-${index}`} style={{ 
            marginBottom: '4px',
            padding: '4px 8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '0px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ 
              fontWeight: 'bold',
              color: colorClass === 'good' ? '#37b24d' : colorClass === 'bad' ? '#ff6b6b' : '#6c757d',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>{item.groupName}</span>
                              {item.rating && colorClass === 'good' && (
                  <span style={{ 
                    fontSize: '0.85rem',
                    padding: '2px 6px',
                    backgroundColor: colorClass === 'good' ? '#d4edda' : '#e9ecef',
                    borderRadius: '12px',
                    color: '#495057'
                  }}>
                    Rating {item.rating}
                  </span>
                )}
            </div>
            <div style={{ 
              fontSize: '0.85rem',
              color: '#6c757d',
              marginTop: '2px',
              paddingLeft: '8px'
            }}>
              {item.companies.length} companies
            </div>
            {item.timestamp && (
              <div style={{ 
                fontSize: '0.85rem',
                color: '#6c757d',
                marginTop: '2px',
                paddingLeft: '8px'
              }}>
                Added: {formatDate(item.timestamp)}
              </div>
            )}
          </div>
        );
      } else {
        // This is an individual company
        const companyLabel = typeof item === 'string' 
          ? item 
          : item.label || item.name || '';
        const companyTimestamp = item.timestamp;
        const companyRating = item.rating;
        
        return (
          <div key={`company-${companyLabel}-${index}`} style={{ 
            marginBottom: '4px',
            padding: '2px 4px',
            backgroundColor: '#f5f5f5',
            borderRadius: '0px',
            color: colorClass === 'good' ? '#37b24d' : colorClass === 'bad' ? '#ff6b6b' : '#6c757d'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong>{companyLabel}</strong>
              {companyRating && (
                <span style={{ 
                  fontSize: '0.85rem',
                  padding: '2px 6px',
                  backgroundColor: colorClass === 'good' ? '#d4edda' : '#e9ecef',
                  borderRadius: '12px',
                  color: '#495057'
                }}>
                  Rating {companyRating}
                </span>
              )}
            </div>
            {companyTimestamp && (
              <div style={{ 
                fontSize: '0.85rem',
                color: '#666',
                marginLeft: '12px',
                borderLeft: '2px solid #ddd',
                paddingLeft: '6px'
              }}>
                Added: {formatDate(companyTimestamp)}
              </div>
            )}
          </div>
        );
      }
    });
  };

  return (
    <div className="content-wrapper page">
      <div className='content-wrapper'>
        <h1>Talent Rating</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px' }}>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className='inputtext search'
          />
        </div>

        <div style={{ display: 'flex', marginTop: '10px' }}>
        <div style={{ marginRight: '10px' }}>
          <Select
            value={typeOptions.find(option => option.value === selectedType)}
            onChange={handleTypeChange}
            options={typeOptions}
            isClearable
            placeholder="Select Type"
            styles={{
              ...customStyles,
              control: (base) => ({
                ...customStyles.control(base),
                minHeight: "60px"
              }),
              option: (base, state) => ({
                ...customStyles.option(base, state),
                minHeight: "60px",
                padding: "14px 12px"
              })
            }}
            />
        </div>
<div>
          <Select
            value={functionOptions.find(option => option.value === selectedFunction)}
            onChange={handleFunctionChange}
            options={functionOptions}
            isClearable
            placeholder="Select Function"
            styles={{
              ...customStyles,
              control: (base) => ({
                ...customStyles.control(base),
                minHeight: "60px"
              }),
              option: (base, state) => ({
                ...customStyles.option(base, state),
                minHeight: "60px",
                padding: "14px 12px"
              })
            }}
          />
        </div>
        <div>
            <Select
              isMulti
              value={filterGoodGroups}
              onChange={setfilterGoodGroups}
              options={companyOptions}
              isClearable
              placeholder="Select Good Performance"
              styles={{
                ...customStyles,
                control: (base) => ({
                  ...customStyles.control(base),
                  minHeight: "60px"
                }),
                option: (base, state) => ({
                  ...customStyles.option(base, state),
                  minHeight: "60px",
                  padding: "14px 12px"
                })
              }}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filtertryoutGroups}
              onChange={setfilterTyoutGroups}
              options={companyOptions}
              isClearable
              placeholder="Select Try Out"
              styles={{
                ...customStyles,
                control: (base) => ({
                  ...customStyles.control(base),
                  minHeight: "60px"
                }),
                option: (base, state) => ({
                  ...customStyles.option(base, state),
                  minHeight: "60px",
                  padding: "14px 12px"
                })
              }}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filterBadGroups}
              onChange={setfilterBadGroups}
              options={companyOptions}
              isClearable
              placeholder="Select Bad Performance"
              styles={{
                ...customStyles,
                control: (base) => ({
                  ...customStyles.control(base),
                  minHeight: "60px"
                }),
                option: (base, state) => ({
                  ...customStyles.option(base, state),
                  minHeight: "60px",
                  padding: "14px 12px"
                })
              }}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filterLocation}
              onChange={setfilterloation}
              options={locationOptions}
              isClearable
              placeholder="Select Location"
              styles={{
                ...customStyles,
                control: (base) => ({
                  ...customStyles.control(base),
                  minHeight: "60px"
                }),
                option: (base, state) => ({
                  ...customStyles.option(base, state),
                  minHeight: "60px",
                  padding: "14px 12px"
                })
              }}
            />
          </div>
          <div style={{ marginLeft: "10px", display: "flex", alignItems: "center" }}>
            <button 
              className='button'
              onClick={toggleAllRows}
              style={{minHeight:'60px'}}
            >
              {isExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
          </div>
      </div>
      
      <div className='content-wrapper content-wrapper-talent-list'>
        <div className="talent-list" style={{ fontSize: '0.85rem' }}>
          <div className='talent-item-header'>
            <div style={{ flexBasis: '250px' }}>Name</div>
            <div style={{ flexBasis: '120px' }}>Type</div>
            <div style={{ flexBasis: '150px' }}>Function</div>
            <div style={{ flexBasis: '120px' }}>Location</div>
            <div style={{ flexBasis: '250px' }}>Good Performance</div>
            <div style={{ flexBasis: '250px' }}>Bad Performance</div>
            <div style={{ flexBasis: '250px' }}>Try Out</div>
            <div style={{ flexBasis: '250px' }}>Notes Product Leads</div>
            <div style={{ flexBasis: '100px' }}></div>
          </div>
          
          {isLoading ? (
            <div className="loading">Loading talents...</div>
          ) : (
            talents.sort((a, b) => {
              const nameA = (a.name || a.Name || '').toLowerCase();
              const nameB = (b.name || b.Name || '').toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
          }).map((talent) => (
            <div key={talent.id} className='talent-item'>
              {isEditing && currentTalent.id === talent.id ? (
                  <div className="talent-item-edit-form">
                    <span style={{ padding: '20px 0', zIndex: '100' }} className='talent-item-header'>{currentName}</span>
          
                    <div className="edit-form-grid">
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.good}>Good Performance Company Groups</span>
                        <GroupWithRatingSelector
                          selectedGroups={selectedGoodGroups}
                          onGroupsChange={setSelectedGoodGroups}
                          placeholder="Select Good Performance Groups"
                          performanceType="good"
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.good}>Individual Good Performance Companies</span>
                        
                        {/* Add new individual companies */}
                        <IndividualCompanySelector
                          selectedCompanies={selectedGoodCompanies}
                          onCompaniesChange={setSelectedGoodCompanies}
                          performanceType="good"
                          placeholder="Select individual companies to add"
                        />
                        
                        {/* Manage existing individual companies */}
                        {selectedGoodCompanies.length > 0 && (
                          <div style={{ 
                            marginBottom: '10px',
                            padding: '8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '0',
                            border: '1px solid #dee2e6'
                          }}>
                            <div style={{ 
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              marginBottom: '8px',
                              color: '#495057'
                            }}>
                              Current Individual Companies ({selectedGoodCompanies.length}):
                            </div>
                            <Select
                              isMulti
                              value={selectedGoodCompanies}
                              onChange={setSelectedGoodCompanies}
                              options={selectedGoodCompanies} // Show current companies for removal
                              isClearable
                              placeholder="Remove companies by clicking ×"
                              styles={customStyles}
                            />
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                              You can remove individual companies by clicking the × next to them above
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.bad}>Bad Performance Company Groups</span>
                        <GroupWithRatingSelector
                          selectedGroups={selectedBadGroups}
                          onGroupsChange={setSelectedBadGroups}
                          placeholder="Select Bad Performance Groups"
                          performanceType="bad"
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.bad}>Individual Bad Performance Companies</span>
                        
                        {/* Add new individual companies */}
                        <IndividualCompanySelector
                          selectedCompanies={selectedBadCompanies}
                          onCompaniesChange={setSelectedBadCompanies}
                          performanceType="bad"
                          placeholder="Select individual companies to add"
                        />
                        
                        {/* Manage existing individual companies */}
                        {selectedBadCompanies.length > 0 && (
                          <div style={{ 
                            marginBottom: '10px',
                            padding: '8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '0px',
                            border: '1px solid #dee2e6'
                          }}>
                            <div style={{ 
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              marginBottom: '8px',
                              color: '#495057'
                            }}>
                              Current Individual Companies ({selectedBadCompanies.length}):
                            </div>
                            <Select
                              isMulti
                              value={selectedBadCompanies}
                              onChange={setSelectedBadCompanies}
                              options={selectedBadCompanies} // Show current companies for removal
                              isClearable
                              placeholder="Remove companies by clicking ×"
                              styles={customStyles}
                            />
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                              You can remove individual companies by clicking the × next to them above
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.base}>Try Out Company Groups</span>
                        <GroupWithRatingSelector
                          selectedGroups={selectedtryoutGroups}
                          onGroupsChange={setSelectedtryoutGroups}
                          placeholder="Select Try Out Groups"
                          performanceType="tryout"
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title' style={titleStyles.base}>Individual Try Out Companies</span>
                        
                        {/* Add new individual companies */}
                        <IndividualCompanySelector
                          selectedCompanies={selectedtryoutCompanies}
                          onCompaniesChange={setSelectedtryoutCompanies}
                          performanceType="tryout"
                          placeholder="Select individual companies to add"
                        />
                        
                        {/* Manage existing individual companies */}
                        {selectedtryoutCompanies.length > 0 && (
                          <div style={{ 
                            marginBottom: '10px',
                            padding: '8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '0px',
                            border: '1px solid #dee2e6'
                          }}>
                            <div style={{ 
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              marginBottom: '8px',
                              color: '#495057'
                            }}>
                              Current Individual Companies ({selectedtryoutCompanies.length}):
                            </div>
                            <Select
                              isMulti
                              value={selectedtryoutCompanies}
                              onChange={setSelectedtryoutCompanies}
                              options={selectedtryoutCompanies} // Show current companies for removal
                              isClearable
                              placeholder="Remove companies by clicking ×"
                              styles={customStyles}
                            />
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                              You can remove individual companies by clicking the × next to them above
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="edit-form-field">
                        <span className="talent-item-input-title" style={titleStyles.base}>Notes Product Leads</span>
                        <input
                          className="inputtext"
                          value={notesProductLeads}
                          onChange={(e) => setNotesProductLeads(e.target.value)}
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className="talent-item-input-title" style={titleStyles.base}>Notes Type</span>
                        <Select
                          value={notesProductLeadsdd}
                          onChange={setNotesProductLeadsdd}
                          options={notesOption}
                          isClearable
                          placeholder="Notes Type"
                          styles={{
                            ...customStyles,
                            control: (base) => ({
                              ...customStyles.control(base),
                              margin: '0'
                            })
                          }}
                        />
                      </div>
                    </div>

                    <div className="edit-form-actions">
                  <button className='button' onClick={updateTalentDetails} style={{ marginTop: '10px', marginBottom: '20px' }}>Save</button>
                  <button className='button' onClick={() => setIsEditing(false)} style={{ marginTop: '10px', marginBottom: '20px' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='talent-item-firstline'>
                      <div style={{ flexBasis: '250px', fontSize: '0.85rem' }}>{talent.name || talent.Name}</div>
                      <div style={{ flexBasis: '120px', fontSize: '0.85rem' }}>{talent.type || talent.bereich || talent.Bereich}</div>
                      <div style={{ flexBasis: '150px', fontSize: '0.85rem' }}>{talent.function || talent.Funktion}</div>
                      <div style={{ flexBasis: '120px', fontSize: '0.85rem' }}>
                        {Array.isArray(talent.locations) 
                          ? talent.locations.map(loc => 
                              typeof loc === 'string' ? loc : 
                              typeof loc === 'object' && loc !== null ? 
                                (loc.short || loc.name || loc.label || loc.city || '[Unknown]') : 
                                String(loc || '')
                            ).join(', ')
                          : (talent.locations || '')}
                      </div>
                  <div style={{ flexBasis: '250px' }}>
                    <div onClick={() => toggleRow(talent.id)} style={{ cursor: 'pointer' }}>
                      {expandedRows.has(talent.id) 
                        ? renderCompanyDisplay(talent.goodPerformanceCompany, 'good')
                        : <TruncatedContent 
                            content={talent.goodPerformanceCompany} 
                            type="companies" 
                            isGoodPerformer={true}
                            isExpanded={false}
                            onToggle={() => toggleRow(talent.id)}
                          />
                      }
                    </div>
                  </div>
                  <div style={{ flexBasis: '250px' }}>
                    <div onClick={() => toggleRow(talent.id)} style={{ cursor: 'pointer' }}>
                      {expandedRows.has(talent.id) 
                        ? renderCompanyDisplay(talent.badPerformanceCompany, 'bad')
                        : <TruncatedContent 
                            content={talent.badPerformanceCompany} 
                            type="companies" 
                            isBadPerformer={true}
                            isExpanded={false}
                            onToggle={() => toggleRow(talent.id)}
                          />
                      }
                    </div>
                  </div>
                  <div style={{ flexBasis: '250px' }}>
                    <div onClick={() => toggleRow(talent.id)} style={{ cursor: 'pointer' }}>
                      {expandedRows.has(talent.id) 
                        ? renderCompanyDisplay(talent.tryOutCompany || talent.tryout_company, 'tryout')
                        : <TruncatedContent 
                            content={talent.tryOutCompany || talent.tryout_company} 
                            type="companies" 
                            isExpanded={false}
                            onToggle={() => toggleRow(talent.id)}
                          />
                      }
                    </div>
                  </div>
                  <div style={{ flexBasis: '250px' }}>  
                    <TruncatedContent 
                      content={`${talent.notesProductLeads || ''} ${talent.notesProductLeadsdd ? `- ${talent.notesProductLeadsdd.label || ''}` : ''}`}
                      type="notes"
                      isExpanded={expandedRows.has(talent.id)}
                      onToggle={() => toggleRow(talent.id)}
                    />
                  </div>
                      <div style={{ flexBasis: '100px', display: "flex", gap: "8px" }}>
                        <button
                          className="toggle-buttons"
                          onClick={() => toggleRow(talent.id)}
                        >
                          <img
                            src={dropdown}
                            alt={expandedRows.has(talent.id) ? "Collapse" : "Expand"}
                            className="edit-container"
                            style={{
                              transform: expandedRows.has(talent.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease'
                            }}
                          />
                        </button>
                        <button className='edit-button' onClick={() => editTalent(talent)}>
                          <img src={edit} alt="Edit" className="edit-container" />
                        </button>
                      </div>
                    </div>


                  </>
                )}
                  </div>
            ))
              )}
        </div>
      </div>
    </div>
  );
}

export default TalentListPlaniing;